import React from 'react';
import LoadingState from './LoadingState';
import ErrorState from './ErrorState';
import EmptyState from './EmptyState';
import ImageList from './ImageList';
import IFrameRenderer from '../../../../IFrameRenderer';
import { DEFAULT_CONFIG } from '../constants';
import { getDisplayImages, SECTION_KEYS } from '../utils/selectionUtils';
import { ImageGroup } from '../../types';

interface RightSectionProps {
  selectedKey?: string;
  iframeUrl?: string | null;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  imageGroups?: ImageGroup[];
  imageURLDetailsByTimestamp: Record<
    string,
    {
      address?: string;
      date?: string;
      time?: string;
      latitude?: string;
      longitude?: string;
      file?: string;
      timestamp?: string;
    }
  >;
  showProImagesSection?: boolean;
  classes: any;
}

const RightSection: React.FC<RightSectionProps> = ({
  selectedKey,
  iframeUrl,
  loading,
  error,
  onRetry,
  imageGroups = [],
  imageURLDetailsByTimestamp,
  showProImagesSection = false,
  classes
}) => {
  const isCarrierImagesSelected = selectedKey === SECTION_KEYS.CARRIER_IMAGES;
  const isProImagesSelected = showProImagesSection && (
    selectedKey === SECTION_KEYS.PRO_IMAGES ||
    (selectedKey && selectedKey !== SECTION_KEYS.CARRIER_IMAGES && imageGroups.some(group => group.key === selectedKey))
  );
  
  const showIframe = isCarrierImagesSelected && !loading;
  const hasNoImages = imageGroups.length === 0 && isProImagesSelected;

  const loadingStateClasses = {
    loadingContainer: classes.loadingContainer,
    loadingMessage: classes.loadingMessage,
  };

  const errorStateClasses = {
    errorContainer: classes.errorContainer,
    errorMessage: classes.errorMessage,
    noData: classes.noData,
  };

  const emptyStateClasses = {
    emptyContainer: classes.emptyContainer,
    noData: classes.noData,
  };

  const renderContent = () => {
    if (!isProImagesSelected) {
      return null;
    }
    
    if (loading) return <LoadingState classes={loadingStateClasses} message={DEFAULT_CONFIG.loading.message} />;
    if (error) {
      return (
        <ErrorState
          classes={errorStateClasses}
          error={error}
          onRetry={onRetry}
          retryButtonText={DEFAULT_CONFIG.error.retryButtonText}
        />
      );
    }
    if (hasNoImages) return <EmptyState classes={emptyStateClasses} message={DEFAULT_CONFIG.empty.message} />;

    const { images, label } = getDisplayImages(selectedKey || '', imageGroups);
    
    return (
      <div className={classes.imageContainer}>
        <ImageList
          key={selectedKey}
          images={images}
          cacheKey={selectedKey || ''}
          label={label}
          imageURLDetailsByTimestamp={imageURLDetailsByTimestamp}
        />
      </div>
    );
  };

  return (
    <div className={`${classes.imagesContainer} customScroll`}>
      {iframeUrl && (
        <div
          className={`${classes.imageContainerIframe} ${!showIframe ? classes.imageContainerIframeHidden : ''}`}>
          <IFrameRenderer url={iframeUrl} visible={showIframe} />
        </div>
      )}
      <div
        className={`${classes.contentWrapper} ${showIframe ? classes.contentWrapperHidden : ''}`}>
        {renderContent()}
      </div>
    </div>
  );
};

export default RightSection;
